import { Button, Checkbox, Heading, Icon, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, Paragraph, PasswordForm, resetUserInactivityTimeout, useToast } from "@aidkitorg/component-library";
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";
import React, { useContext, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useHistory } from "react-router-dom";
import { get_deployment, usePost } from "./API";
import { LanguageDropdown } from "./Components/LanguageDropdown";
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext, UserInfoContext } from "./Context";
import { useLocalizedStrings } from "./Localization";
import { AidKitLogo, classNames, useMarkdown } from "./Util";
import { generatePasskeyLabel, passkeyBackendErrorKind, passkeyDismissalStorageKey, passkeyErrorKind, PasskeyErrorKind, passkeysEnabled, shouldPromptForPasskeyEnrollment, shouldRequestPasskeyMethodSelection, shouldShowEmailCodeOption } from "./passkeys";

type LoginDisplayMode = 'EmailOnly' | 'SelectMethod' | 'PasskeyError' | 'EnrollPasskey' | 'VerifyingCode' | 'EnrollPassword' | 'VerifyPassword' | 'ForgotPassword' | 'ForgotPasswordConfirmation' | 'Blocked' | 'SSOPopup';

// NOTE: This is only used in development
const DevCopyCode: React.FC<{ code: string; dismiss?: () => void }> = ({ code, dismiss }) => {
  const [copied, setCopied] = useState<boolean>(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
  };

  return (
    <div className="flex flex-row gap-2 min-w-[225px]">
      <Button onClick={handleCopy} icon={<Icon name="DocumentDuplicateIcon" />}>Copy Code</Button>
      {copied && <small className="flex flex-row gap-1 items-center"><Icon name="CheckCircleIcon" color="#15803d" type="solid" /> Copied</small>}
    </div>
  );
};

const AuthPage: React.FC<{ authStrategy?: 'session' | 'user', onLoginSuccess?: () => void }> = ({ authStrategy = 'user', onLoginSuccess }) => {
  const { toast } = useToast();
  const userContext = useContext(UserInfoContext);
  const context = useContext(InterfaceContext);
  const configuration = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const history = useHistory();
  const L = useLocalizedStrings();
  const [, setCookie,] = useCookies(["auth_token", "auth_token_expires"]);

  // Default login API routes
  const sendVerification = usePost("/authenticate");
  const verifyCode = usePost("/authenticate/complete", {
    token: () => verificationToken.current
  });
  const passkeyAuthenticationOptions = usePost('/authenticate/passkey/authentication/options', { suppressErrorDisplay: true });
  const passkeyAuthenticationComplete = usePost('/authenticate/passkey/authentication/complete', { suppressErrorDisplay: true });
  const postLoginToken = useRef('');
  const passkeyRegistrationOptions = usePost('/authenticate/passkey/registration/options', {
    token: () => postLoginToken.current,
    suppressErrorDisplay: true,
  });
  const passkeyRegistrationComplete = usePost('/authenticate/passkey/registration/complete', {
    token: () => postLoginToken.current,
    suppressErrorDisplay: true,
  });
  const passkeyList = usePost('/authenticate/passkey/list', {
    token: () => postLoginToken.current,
    suppressErrorDisplay: true,
  });

  // Session-based token "Modern Auth" API routes for 'session' authStrategy
  const sendConfirmation = usePost("/confirm/phone");
  const login = usePost("/login");
  const sessionPasskeyMethodSelection = usePost('/authenticate/passkey/method-selection', { suppressErrorDisplay: true });

  // Session-based token (only for /signin path) for 'session' authStrategy
  const [token, setToken] = React.useState<string>('');

  // Control for login display
  const [loginDisplayMode, setLoginDisplayMode] = useState<LoginDisplayMode>('EmailOnly')

  // Password two-factor Auth
  const enrollPassword = usePost("/authenticate/password/enroll", {
    token: () => verificationToken.current 
  })
  const verifyPassword = usePost("/authenticate/verify_password", {
    token: () => verificationToken.current
  })
  const requestResetPassword = usePost("/authenticate/password/forgot", {
    token: () => verificationToken.current
  });
  const [passwordError, setPasswordError] = useState<boolean>(false)
  const [password, setPassword] = useState<string>("");
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>("");

  const [code, setCode] = useState<string>("");
  const [verifyingCode, setVerifyingCode] = useState<boolean>(false);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [passkeyError, setPasskeyError] = useState<PasskeyErrorKind | null>(null);
  // Named for the enrollment environment rather than prompting for another
  // input in a flow the user is already being interrupted with.
  const passkeyLabel = useMemo(() => generatePasskeyLabel({
    locale: context.lang,
    environment: navigator,
  }), [context.lang]);
  // Only meaningful under mandatoryPasskeys: whether the account behind the
  // current method-selection screen is confirmed enrolled (so "use a code
  // instead" would be a dead end) vs. just being opportunistically offered
  // passkey first (where the code fallback is real, for bootstrap).
  const [methodSelectionEnrolled, setMethodSelectionEnrolled] = useState<boolean | null>(null);

  const [emailFormData, setEmailFormData] = useState<{ email: string, emailOnly: boolean }>({ email: '', emailOnly: false });
  const [loading, setLoading] = useState<boolean>(false);
  const [ssoRedirectUrl, setSSORedirectUrl] = useState<string>('');
  const [ssoPopup, setSSOPopup] = useState<Window | null>(null);
  const verificationToken = useRef("");
  const toastDismissRef = useRef<(() => void) | null>(null);

  const wasIdle = location.search.includes("&reason=idle")
  // Use RegEx to remove the reason=idle flag
  const next = decodeURIComponent(location.search.slice("?next=".length)).replace(new RegExp('&reason=idle$'), '');

  const resetOTPCode = () => {
    setLoginDisplayMode('EmailOnly')
    setVerifyingCode(false);
    setCodeError(false);
    setCode("");
    setDevCode(null);
    setPasskeyError(null);
    setMethodSelectionEnrolled(null);

    if (toastDismissRef.current) {
      toastDismissRef.current();
    }
  }

  // Never read popup.closed or call popup.close() from the opener — COOP blocks both in Arc and others.
  // Clear our reference only; the popup closes itself from inside (callback calls window.close()).

  // Open SSO popup window
  const ssoPopupInterval = useRef<NodeJS.Timeout | null>(null);
  const openSSOPopup = (url: string) => {
    const popup = window.open(
      url,
      'sso-auth',
      'width=500,height=600,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no'
    );
    
    if (!popup) {
      toast({
        description: 'Popup blocked. Please allow popups for this site and try again.',
        variant: 'error'
      });
      setLoginDisplayMode('EmailOnly');
      return;
    }

    setSSOPopup(popup);
  };

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only trust messages from our own origin
      if (event.origin !== window.location.origin) {
        console.log('Rejected message from origin:', event.origin);
        return;
      }
  
      const expected = sessionStorage.getItem('oauth_state_token');
      const data = event.data || {};
  
      if (data.type === 'SSO_SUCCESS') {
        if (!data.state || data.state !== expected) {
          console.warn('SSO state mismatch');
          return;
        }
        sessionStorage.removeItem('oauth_state_token');
        if (ssoPopup) setSSOPopup(null);
        setLoginDisplayMode('EmailOnly');
        handleAuthenticatedUser(data.token);
      } else if (data.type === 'SSO_ERROR') {
        if (ssoPopup) setSSOPopup(null);
        setLoginDisplayMode('EmailOnly');
        toast({ description: data.message || 'SSO authentication failed', variant: 'error' });
      }
    };
  
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (ssoPopup) setSSOPopup(null);
      if (ssoPopupInterval.current) clearInterval(ssoPopupInterval.current);
    };
  }, [ssoPopup]);

  // Safari (and others) can clear window.opener, so the popup never postMessages. When the user
  // returns to this window (focus), the popup has set the auth cookie; refresh and close the modal.
  React.useEffect(() => {
    const handleFocus = () => {
      if (loginDisplayMode !== 'SSOPopup') return;
      userContext?.refreshContext?.();
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        history.push(next || (publicConfig.adminPanelEnabled ? '/admin-panel/home' : '/'));
      }
      setSSOPopup(null);
      setLoginDisplayMode('EmailOnly');
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loginDisplayMode, userContext, onLoginSuccess, next, publicConfig, history]);

  const onCodeChange = (newValue: string) => {
    setCode(newValue);
    setCodeError(false);
  }

  const showOtpVerification = (result: any) => {
    if (result?.popup) {
      if (onLoginSuccess) {
        setDevCode(result.popup);
      } else {
        const { dismiss } = toast({
          description: result.popup,
          action: <DevCopyCode code={result.popup} dismiss={() => { dismiss() }} />,
          autoClose: false,
        });
        toastDismissRef.current = dismiss;
      }
    }
    verificationToken.current = result?.intermediate_token || '';
    setLoginDisplayMode('VerifyingCode');
  };

  const showPasskeyMethodSelection = (result: any) => {
    if (!result?.method_selection) return false;
    datadogRum.addAction('passkey_option_shown');
    setPasskeyError(null);
    setMethodSelectionEnrolled(result.enrolled ?? null);
    setLoginDisplayMode('SelectMethod');
    return true;
  };

  const beginSessionCodeLogin = async () => {
    const result = await sendConfirmation({ phone: emailFormData.email, language: 'en' });
    if (result.token) {
      setToken(result.token);
      setLoginDisplayMode('VerifyingCode');
      return;
    }
    toast({
      description: 'Error authenticating',
      variant: 'error'
    });
  };

  const handleEmailFormSubmission = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const form = e.target as HTMLFormElement;
    // Let HTML5 validation kick in within the browser and don't do anything else if checkValidity is false
    if (form.checkValidity()) {
      if (authStrategy === 'session') {
        if (publicConfig.adminPasskeys) {
          const methodSelection = await sessionPasskeyMethodSelection({
            email: emailFormData.email,
            passkey_supported: browserSupportsWebAuthn(),
          });
          if (showPasskeyMethodSelection(methodSelection)) {
            setLoading(false);
            return;
          }
        }
        await beginSessionCodeLogin();
        setLoading(false);
      } else {
        const result = await sendVerification({
          email: emailFormData.email,
          email_only: emailFormData.emailOnly,
          open_in_popup: !!onLoginSuccess,
          passkey_supported: shouldRequestPasskeyMethodSelection(
            passkeysEnabled(publicConfig),
            browserSupportsWebAuthn(),
            authStrategy,
          ),
        });
        setLoading(false);

        // Google SSO Redirection
        if (result?.redirect) {
          if (!result.oAuthStateToken) {
            // Missing OAuth state token from server
            toast({
              description: 'Error authenticating SSO',
              variant: 'error'
            })
            return;
          } else {
            // Temporarily stores OAuth state token to compare later when callback is received
            sessionStorage.setItem('oauth_state_token', result.oAuthStateToken);
            
            // If we're in a modal (onLoginSuccess is provided), use popup instead of redirect
            if (onLoginSuccess) {
              setLoginDisplayMode('SSOPopup' as any);
              setSSORedirectUrl(result.redirect);
              openSSOPopup(result.redirect);
              return;
            } else {
              // Full page redirect for non-modal usage. The return path lives in this
              // origin's sessionStorage and is kept out of the OAuth state: long paths
              // (import signatures, encoded Explore queries) would push the callback query
              // string past the CloudFront WAF's 2 KB SizeRestrictions_QUERYSTRING limit.
              if (next) {
                sessionStorage.setItem('oauth_next', next);
              } else {
                sessionStorage.removeItem('oauth_next');
              }
              window.location.href = result.redirect;
              return;
            }
          }
        }

        if (showPasskeyMethodSelection(result)) return;

        showOtpVerification(result);
      }
    } else {
      setLoading(false);
      return;
    }
  }

  const storeAuthenticatedUser = (token: string | undefined) => {
    if (token) resetUserInactivityTimeout();
    // Create auth token that self-expires after 86400 seconds (~1 rotation of the earth -- 24 hours)
    const expiresIn = 86400;
    setCookie("auth_token", token, {
      maxAge: expiresIn,
      path: "/",
      sameSite: "strict"
    });
    setCookie("auth_token_expires", Date.now() + expiresIn * 1000, {
      maxAge: expiresIn,
      path: "/",
      sameSite: "strict"
    })

    // Causes the UserInfoWrapper which provides UserInfoContext to call /user/info again now that we have a verified token
    if (userContext?.refreshContext) {
      userContext.refreshContext();
    } else {
      Sentry.captureMessage('Could not refresh UserInfoContext, callback missing', 'warning');
    }

    // Dismiss temp local code toast if it exists
    if (toastDismissRef.current) {
      toastDismissRef.current();
    }

  }

  const navigateAfterAuthentication = () => {
    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      history.push(next || (publicConfig.adminPanelEnabled ? '/admin-panel/home' : '/'));
    }
  }

  const handleAuthenticatedUser = (token: string | undefined) => {
    storeAuthenticatedUser(token);
    navigateAfterAuthentication();
  }

  const enrollmentDismissalKey = () => passkeyDismissalStorageKey(get_deployment(), emailFormData.email);

  const handleLegacyAuthenticatedUser = async (token: string | undefined) => {
    storeAuthenticatedUser(token);
    const mandatory = !!publicConfig.mandatoryPasskeys;
    // Non-mandatory deployments keep the original bypasses: skip entirely from
    // the session-renewal modal (onLoginSuccess) or on a browser that can't do
    // WebAuthn. Under mandatoryPasskeys neither bypass applies — enrollment is
    // enforced from that modal too, and an unsupported browser still gets the
    // prompt so it can fail visibly with a "contact an admin" message instead
    // of silently slipping through.
    if (
      !token
      || !passkeysEnabled(publicConfig)
      || (!mandatory && (onLoginSuccess || !browserSupportsWebAuthn()))
    ) {
      navigateAfterAuthentication();
      return;
    }

    postLoginToken.current = token;
    try {
      const result = await passkeyList({});
      const dismissedAt = window.localStorage.getItem(enrollmentDismissalKey());
      if ('passkeys' in result && shouldPromptForPasskeyEnrollment({
        enabled: true,
        supported: browserSupportsWebAuthn(),
        passkeyCount: result.passkeys?.length ?? 0,
        dismissedAt,
        mandatory,
      })) {
        datadogRum.addAction('passkey_enrollment_shown');
        setPasskeyError(null);
        setLoginDisplayMode('EnrollPasskey');
        return;
      }
    } catch (error) {
      console.warn('Unable to check passkey enrollment status', error);
    }
    navigateAfterAuthentication();
  }

  const handleUseCode = async () => {
    datadogRum.addAction('passkey_fallback_selected');
    setLoading(true);
    setPasskeyError(null);

    if (authStrategy === 'session') {
      const methodSelection = await sessionPasskeyMethodSelection({
        email: emailFormData.email,
        passkey_supported: true,
        use_code: true,
      });
      if (!showPasskeyMethodSelection(methodSelection)) {
        await beginSessionCodeLogin();
      }
      setLoading(false);
      return;
    }

    const result = await sendVerification({
      email: emailFormData.email,
      email_only: emailFormData.emailOnly,
      open_in_popup: !!onLoginSuccess,
      passkey_supported: true,
      use_code: true,
    });
    setLoading(false);
    // The account has a passkey enrolled, so the server declined the code
    // fallback and handed back the same method-selection shape instead of an
    // OTP. Send the user back to picking a method rather than an empty code screen.
    if (result?.method_selection) {
      // The server only ever returns method_selection here for an enrolled
      // account (an unenrolled account's use_code request goes through to a
      // real OTP instead), so default to true if the mandatory-mode flag is
      // absent for some reason.
      setMethodSelectionEnrolled(result.enrolled ?? true);
      setLoginDisplayMode('SelectMethod');
      return;
    }
    showOtpVerification(result);
  };

  const handleUsePasskey = async () => {
    datadogRum.addAction('passkey_authentication_started');
    setLoading(true);
    setPasskeyError(null);
    try {
      const optionsResult = await passkeyAuthenticationOptions({ email: emailFormData.email });
      if (!('options' in optionsResult) || !optionsResult.options || !optionsResult.ceremony_id) {
        throw new Error('Missing passkey authentication options');
      }
      const response = await startAuthentication({ optionsJSON: optionsResult.options });
      const completion = await passkeyAuthenticationComplete({
        ceremony_id: optionsResult.ceremony_id,
        response,
        authStrategy,
      });
      if ('auth_token' in completion && completion.auth_token) {
        datadogRum.addAction('passkey_authentication_succeeded');
        handleAuthenticatedUser(completion.auth_token);
        return;
      }
      if ('error' in completion) {
        // The program only lets admins sign in, so offering another attempt would mislead.
        if (completion.error === 'program_no_longer_active') {
          datadogRum.addAction('passkey_authentication_blocked');
          resetOTPCode();
          return;
        }
        const kind = passkeyBackendErrorKind(completion.error);
        datadogRum.addAction(
          kind === 'expired' ? 'passkey_authentication_expired'
            : kind === 'disabled' ? 'passkey_authentication_disabled'
              : 'passkey_authentication_failed'
        );
        setPasskeyError(kind);
        setLoginDisplayMode('PasskeyError');
        return;
      }
      throw new Error('Missing passkey authentication completion result');
    } catch (error: any) {
      const kind = passkeyErrorKind(error);
      datadogRum.addAction(kind === 'cancelled' ? 'passkey_authentication_cancelled' : 'passkey_authentication_failed');
      setPasskeyError(kind);
      setLoginDisplayMode('PasskeyError');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollPasskey = async () => {
    datadogRum.addAction('passkey_enrollment_started');
    setLoading(true);
    setPasskeyError(null);
    try {
      const optionsResult = await passkeyRegistrationOptions({});
      if ('error' in optionsResult) {
        const kind = passkeyBackendErrorKind(optionsResult.error);
        datadogRum.addAction(
          kind === 'disabled' ? 'passkey_enrollment_disabled'
            : 'passkey_enrollment_failed',
        );
        setPasskeyError(kind);
        setLoginDisplayMode('PasskeyError');
        return;
      }
      if (!('options' in optionsResult) || !optionsResult.options || !optionsResult.ceremony_id) {
        throw new Error('Missing passkey registration options');
      }
      const response = await startRegistration({ optionsJSON: optionsResult.options });
      const completion = await passkeyRegistrationComplete({
        ceremony_id: optionsResult.ceremony_id,
        label: passkeyLabel,
        response,
      });
      if ('verified' in completion && completion.verified) {
        datadogRum.addAction('passkey_enrollment_succeeded');
        window.localStorage.removeItem(enrollmentDismissalKey());
        navigateAfterAuthentication();
        return;
      }
      throw new Error('Passkey registration was not verified');
    } catch (error: any) {
      const kind = passkeyErrorKind(error);
      datadogRum.addAction(
        kind === 'cancelled' ? 'passkey_enrollment_cancelled'
          : kind === 'unsupported' ? 'passkey_enrollment_unsupported'
            : 'passkey_enrollment_failed'
      );
      setPasskeyError(kind);
    } finally {
      setLoading(false);
    }
  };

  const dismissPasskeyEnrollment = () => {
    datadogRum.addAction('passkey_enrollment_dismissed');
    window.localStorage.setItem(enrollmentDismissalKey(), Date.now().toString());
    navigateAfterAuthentication();
  };

  const handleCodeSubmission = async (code: string) => {
    setVerifyingCode(true);

    if (authStrategy === 'session') {
      const result = await login({ token, answer: code });
      setVerifyingCode(false);

      if (publicConfig.adminPasswords && result?.intermediate_token) {
        verificationToken.current = result.intermediate_token;

        if (result.require_new_password) {
          setLoginDisplayMode('EnrollPassword')
        } else {
          setLoginDisplayMode('VerifyPassword')
        }
      } else if (result?.token) {
        void handleLegacyAuthenticatedUser(result.token);
      } else if (result?.error && result.error === 'challenge_incorrect') {
        setCodeError(true);
        return;
      } else {
        // Return to home and let the API toast errors
        resetOTPCode();
        return;
      }
    } else {
      const result = await verifyCode({
        code: code
      });

      setVerifyingCode(false);

      if (!result || 'error' in result) {
        if (!result || result.error === 'incorrect_code') {
          setCodeError(true);
          return;
        } else {
          // Reset state if verification failed
          verificationToken.current = '';
          setLoginDisplayMode('EmailOnly')
          setCode('');
          return;
        }
      } else {
        if (publicConfig.adminPasswords && 'intermediate_token' in result && result.intermediate_token) {
          // Update intermediate token
          verificationToken.current = result.intermediate_token;

          if (result.require_new_password) {
            setLoginDisplayMode('EnrollPassword')
          } else {
            setLoginDisplayMode('VerifyPassword')
          }
        } else {
          if ('auth_token' in result) {
            void handleLegacyAuthenticatedUser(result.auth_token)
          }
        }
      }
    }
  }

  const handleEnrollPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true)

    const result = await enrollPassword({
      password,
      authStrategy
    })

    if (result?.error) {
      setLoading(false)
      setPassword("")
      setPasswordConfirmation("")

      console.error(result.error)

      return;
    } else {
      setLoading(false) 
      void handleLegacyAuthenticatedUser(result.auth_token)
    }
  }

  const handleVerifyPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true)
    const result = await verifyPassword({
      password,
      authStrategy
    })

    if (!result || 'error' in result) {
      setLoading(false)
      if (!result || result.error == 'incorrect_password') {
        setPasswordError(true)
      } else {
        console.error(result.error)
      }
    } else {
      setLoading(false)
      if ('auth_token' in result) {
        void handleLegacyAuthenticatedUser(result.auth_token)
      }
    }
  }

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    setLoading(true);
    const response = await requestResetPassword({});

    if (response) {
      // Reset the login state once we've declared that we've forgotten our password and gotten a reset email
      setCode("");
      setEmailFormData({ ...emailFormData, email: '' })
      verificationToken.current = ''

      if (response.status === 'ok') {
        setLoginDisplayMode('ForgotPasswordConfirmation')
      }
      if (response.error === 'account_temporary_locked') {
        setLoginDisplayMode('Blocked')
      }
    }

    setLoading(false);
  }

  const AnnouncementBanner = () => {
    const banner = (message: string, color?: 'Red' | 'Yellow' | 'Blue') => {
      const colorConfig: Record<'Red' | 'Yellow' | 'Blue', { bg: string, text: string }> = {
        'Red': { bg: 'bg-red-700', text: 'text-rose-50' },
        'Yellow': { bg: 'bg-yellow-400', text: 'text-amber-950' },
        'Blue': { bg: 'bg-indigo-800', text: 'text-indigo-50' }
      }

      return (
        <div className={`${colorConfig[color ?? 'Blue'].bg} px-6 py-2 flex justify-center`}>
          <Paragraph className={`${colorConfig[color ?? 'Blue'].text} text-sm text-center m-0`} measure="wide">
            {useMarkdown(message)}
          </Paragraph>
        </div>
      );
    }

    if (configuration.announcement) {
      // Prioritize legacy airtable announcement
      return banner(configuration.announcement);
    } else if (wasIdle) {
      // Prioritize inactivity logout banner
      return banner(L.login.idle_logout, 'Blue')
    } else if (context.loginBanner) {
      // Fetch new interface announcement
      return banner(context.loginBanner[context.lang], context.loginBanner.color as 'Red' | 'Yellow' | 'Blue');
    }

    // Return null if no announcement banner
    return null;
  };

  const EmailLogin = (
    <form onSubmit={handleEmailFormSubmission} className="flex flex-col min-h-[160px]">
      <div className="flex-grow w-full space-y-2 py-1">
        <label htmlFor="email" className="block text-sm font-medium">
          {L.auth.email_address}
        </label>
        <div className="mt-2">
          <input
            id="email"
            name="email"
            type="email"
            value={emailFormData.email}
            onChange={(e) => setEmailFormData({ ...emailFormData, email: e.target.value })}
            required
            autoComplete="email"
            className="block w-full rounded-md border-0 py-2 px-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6 text-sm md:text-base"
            autoFocus
          />
        </div>
        {/* Mandatory-passkey programs do not offer email-code authentication. */}
        {authStrategy !== 'session' && shouldShowEmailCodeOption(!!publicConfig.mandatoryPasskeys) && (
          <div className="flex items-center">
            <Checkbox
              label={L.auth.send_code_to_email}
              checked={emailFormData.emailOnly}
              onChange={(checked) => setEmailFormData({ ...emailFormData, emailOnly: checked as boolean })}
            />
          </div>
        )}
      </div>
      <div className="flex-grow w-full flex items-end py-1">
        <Button type="submit" variant="primary" className="w-full" loading={loading}>
          {L.auth.submit}
        </Button>
      </div>
    </form>
  );

  // Only hide the code fallback once the server has confirmed this account is
  // actually enrolled — an opportunistic offer to an unenrolled/unknown
  // account still has a real code fallback (that's the enrollment bootstrap
  // path), so it must stay visible even under mandatoryPasskeys.
  const hideCodeFallback = !!publicConfig.mandatoryPasskeys
    && (methodSelectionEnrolled === true || passkeyError === 'disabled');

  const MethodSelection = (
    <div className="flex flex-col min-h-[190px] gap-3">
      <Paragraph className="text-center mt-0 text-gray-700">
        {L.passkeys.choose_method_description}
      </Paragraph>
      <Button type="button" variant="primary" className="w-full" loading={loading} onClick={handleUsePasskey}>
        {L.passkeys.use_passkey}
      </Button>
      {!hideCodeFallback && (
        <Button type="button" variant="secondary" className="w-full" loading={loading} onClick={handleUseCode}>
          {L.passkeys.use_code}
        </Button>
      )}
      <Button type="button" variant="link" className="w-full" onClick={resetOTPCode}>
        {L.auth.back}
      </Button>
    </div>
  );

  const PasskeyFailure = (
    <div className="flex flex-col min-h-[190px] gap-3">
      <Heading level="h3" className="text-center mb-1">
        {passkeyError === 'cancelled' ? L.passkeys.cancelled_title
          : passkeyError === 'expired' ? L.passkeys.expired_title
            : passkeyError === 'disabled' ? L.passkeys.disabled_title
              : passkeyError === 'unsupported' ? L.passkeys.unsupported_title
                : L.passkeys.failed_title}
      </Heading>
      <Paragraph className="text-center mt-0 text-gray-700">
        {passkeyError === 'cancelled' ? L.passkeys.cancelled_description
          : passkeyError === 'expired' ? L.passkeys.expired_description
            : passkeyError === 'disabled' ? L.passkeys.disabled_description
              : passkeyError === 'unsupported' ? L.passkeys.unsupported_description
                : L.passkeys.failed_description}
      </Paragraph>
      {passkeyError !== 'disabled' && passkeyError !== 'unsupported' && (
        <Button type="button" variant="primary" className="w-full" loading={loading} onClick={handleUsePasskey}>
          {L.passkeys.try_again}
        </Button>
      )}
      {!hideCodeFallback && (
        <Button type="button" variant="secondary" className="w-full" loading={loading} onClick={handleUseCode}>
          {L.passkeys.recover_with_code}
        </Button>
      )}
    </div>
  );

  const PasskeyEnrollment = (
    <div className="flex flex-col min-h-[220px] gap-3">
      <Heading level="h3" className="text-center mb-1">{L.passkeys.enrollment_title}</Heading>
      <Paragraph className="text-center mt-0 text-gray-700">{L.passkeys.enrollment_description}</Paragraph>
      <div className="rounded-md bg-slate-50 px-3 py-2 border border-gray-200">
        <div className="text-xs font-medium text-gray-600">{L.passkeys.label}</div>
        <div className="text-sm text-gray-900">{passkeyLabel}</div>
      </div>
      {passkeyError && (
        <Paragraph size="sm" className="text-red-700 mt-0">
          {passkeyError === 'cancelled' ? L.passkeys.cancelled_description
            : passkeyError === 'unsupported' ? L.passkeys.unsupported_description
              : L.passkeys.failed_description}
        </Paragraph>
      )}
      <Button type="button" variant="primary" className="w-full" loading={loading} onClick={handleEnrollPasskey}>
        {L.passkeys.add_passkey}
      </Button>
      {/* No skip option under mandatoryPasskeys — enrollment is required, every login, until it succeeds. */}
      {!publicConfig.mandatoryPasskeys && (
        <Button type="button" variant="link" className="w-full" onClick={dismissPasskeyEnrollment}>
          {L.passkeys.skip_for_now}
        </Button>
      )}
      <Paragraph size="sm" className="text-gray-600 mt-0">
        {publicConfig.mandatoryPasskeys ? L.passkeys.recovery_admin_only : L.passkeys.recovery_available}
      </Paragraph>
    </div>
  );

  const OTPVerify = (
    <div className="flex flex-col min-h-[160px]">
      <div className="flex-grow w-full py-1">
        {verifyingCode && <div className="flex justify-center items-center min-h-[6em]"><div className="w-6 h-6 border-2 border-t-transparent border-blue-200 rounded-full animate-spin mr-2" /></div>}

        {!verifyingCode && (
          <>
            <div className="flex justify-center">
              <InputOTP maxLength={6} autoFocus label={L.auth.verification_code} onComplete={handleCodeSubmission} error={codeError} value={code} onChange={onCodeChange}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <div className="my-4 text-xs text-gray-600 space-y-1">
              {emailFormData.emailOnly ? (
                <>
                  <Paragraph size="sm" className="mb-2 text-gray-600">
                    {L.auth.code_sent_to_email_first.replace('$email', emailFormData.email)}
                  </Paragraph>
                  <Paragraph size="sm" className="mt-3 text-gray-600">
                    {useMarkdown(L.auth.code_sent_to_email_troubleshoot)}
                  </Paragraph>
                </>
              ) : (
                <Paragraph size="sm" className="mb-0 text-gray-600">
                  {L.auth.code_sent_to_phone_or_email.replace('$email', emailFormData.email)}
                </Paragraph>
              )}
            </div>
          </>)
        }
      </div>
      {devCode && (
        <DevCopyCode code={devCode} />
      )}
      <div className="flex-grow flex items-end py-1">
        <Button variant="secondary" className="w-full" onClick={resetOTPCode}>
          {L.auth.back}
        </Button>
      </div>
    </div>
  );

  const enrollPasswordLogin = () => {
    // Validation rules should match with those in packages/aidkit/lib/authentication/passwords.ts
    const uppercaseRegex = /[A-Z]/;
    const lowercaseRegex = /[a-z]/;
    const numberRegex = /\d/;
    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>~`_\+\-=\[\]\\;]/;
  
    const specialCharLabel = (
      <div>
        <p className="mb-0">{L.auth.validation_rules.special_char}</p>
        <p>{'[!@#$%^&*(),.?":{}|<>~`_\+\-=\[\]\\;]'}</p>
      </div>
    )
  
    const validationRules =  [
      { regex: /.{16,}/, label: L.auth.validation_rules.at_least_16_chars},
      { regex: /^.{1,63}$/, label: L.auth.validation_rules.less_than_64_chars},
      { regex: uppercaseRegex, label: L.auth.validation_rules.uppercase},
      { regex: lowercaseRegex, label: L.auth.validation_rules.lowercase},
      { regex: numberRegex, label: L.auth.validation_rules.number},
      { regex: specialCharRegex, label: specialCharLabel},
    ]

    return (
      <div className="flex flex-col w-full min-h-[160px]">
        <p>
          {L.auth.password_notice}
        </p>
        <PasswordForm 
          value={password}
          onValueChange={(e) => setPassword(e.target.value)}
          confirmationValue={passwordConfirmation}
          onConfirmationValueChange={(e) => setPasswordConfirmation(e.target.value)}
          validationRules={validationRules}
          loading={loading}
          onSubmit={handleEnrollPassword}
        />
      </div>
    )
  }

  const passwordErrorMsg = (
    <div className="py-2 flex gap-1 items-center">
      <div>
        <Icon role="decorative" name="XCircleIcon" type="solid" className="text-red-500"/>
      </div>
      <div className="text-sm">
        {L.auth.incorrect_password}
      </div>
    </div>
  )

  const verifyPasswordLogin = (
    <form onSubmit={handleVerifyPassword} className="flex flex-col min-h-[160px]">
      <div className="flex flex-col min-h-[160px]">
        <div className="flex-grow w-full space-y-2 py-1">
          <div className="block text-sm font-medium">
            {L.auth.email_address}
          </div>
          <div className="mt-2">
            {emailFormData.email}
          </div>
          <label htmlFor="password" className="block text-sm font-medium">
            {L.auth.password}
          </label>
          <div className="mt-2">
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={classNames(
                "block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6",
                passwordError ? "outline-red-500" : "outline-gray-300",
              )}
              autoFocus
            />
            <div role="alert">
              {passwordError && passwordErrorMsg}
            </div>
          </div>
        </div>
        <div className="flex-grow flex items-end py-2">
          <Button type="submit" variant="primary" className="w-full" loading={loading}>
            {L.auth.submit}
          </Button>
        </div>
        <div className="pt-2">
          <Button variant="link" onClick={() => {
            setLoginDisplayMode('ForgotPassword')
          }}>
            {L.auth.forgot_password}
          </Button>
        </div>
      </div>
    </form>
  )

  const forgotPassword = (
    <form onSubmit={handleForgotPassword}>
      <Heading level="h3" className="text-center mb-3">
        {L.auth.forgot_password}
      </Heading>
      <Paragraph className="mt-0 mb-1.5">
        {L.auth.forgot_password_prompt}
      </Paragraph>
      <div className="flex-grow w-full space-y-2 py-1">
        <span>{emailFormData.email}</span>
      </div>
      <div className="flex-grow flex items-end pb-2 pt-4">
        <Button type="submit" variant="primary" className="w-full" loading={loading}>
          {L.auth.forgot_password_send}
        </Button>
      </div>
    </form>
  )

  const forgotPasswordConfirmation = (
    <div>
      <Icon name="CheckCircleIcon" role="decorative" size="xl" type="solid" className="mx-auto text-green-800 mb-2" />
      <Heading level="h3" className="text-center mb-3">
        {L.auth.email_sent}
      </Heading>
      <Paragraph className="mt-0">
        {L.auth.email_sent_message}
      </Paragraph>
      <Button variant="primary" className="w-full" onClick={() => {setLoginDisplayMode('EmailOnly')}}>
        {L.auth.return_to_login}
      </Button>
    </div>
  );

  const blocked = (
    <div>
      <Icon name="ExclamationTriangleIcon" role="decorative" size="xl" type="solid" className="mx-auto text-amber-800" />
      <Heading level="h3" className="text-center mb-3">
        {L.auth.reset_blocked_title}
      </Heading>
      <Paragraph className="mt-0">
        {L.auth.reset_blocked_message}
      </Paragraph>
      <Button variant="primary" className="w-full" onClick={() => { setLoginDisplayMode('EmailOnly') }}>
        {L.auth.return_to_login}
      </Button>
    </div>
  )

  const ssoPopupComponent = (
    <div className="flex flex-col min-h-[300px]">
      <div className="flex-grow w-full py-1">
        <div className="text-center mb-6">
          <Icon name="ArrowPathIcon" role="decorative" size="lg" className="mx-auto text-blue-600 animate-spin mb-4" />
          <Heading level="h3" className="text-center mb-2">
            Authenticating with SSO...
          </Heading>
          <Paragraph className="mt-0 text-gray-600 mb-4">
            A popup window has opened for authentication. Please complete the login process in the popup window.
          </Paragraph>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <Icon name="InformationCircleIcon" role="decorative" className="text-blue-600 mr-2" />
              <Paragraph size="sm" className="text-blue-800 m-0">
                If the popup didn't open, please check your browser's popup blocker settings.
              </Paragraph>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow flex items-end py-1">
        <Button 
          variant="secondary" 
          className="w-full" 
          onClick={() => {
            if (ssoPopup) {
              ssoPopup.close();
              setSSOPopup(null);
            }
            setLoginDisplayMode('EmailOnly');
          }}
        >
          {L.auth.back}
        </Button>
      </div>
    </div>
  )

  const loginDisplay = () => {
    switch (loginDisplayMode) {
      case 'EmailOnly': 
        return EmailLogin;
      case 'SelectMethod':
        return MethodSelection;
      case 'PasskeyError':
        return PasskeyFailure;
      case 'EnrollPasskey':
        return PasskeyEnrollment;
      case 'VerifyingCode':
        return OTPVerify;
      case 'EnrollPassword':
        return enrollPasswordLogin();
      case 'VerifyPassword':
        return verifyPasswordLogin;
      case 'ForgotPassword':
        return forgotPassword;
      case 'ForgotPasswordConfirmation':
        return forgotPasswordConfirmation;
      case 'Blocked':
        return blocked;
      case 'SSOPopup':
        return ssoPopupComponent;
    }
  }

  const applicantFacingLogo = publicConfig.interface?.applicantFacingLogo?.url || configuration.applicant_facing_logo;
  const programName = publicConfig.name || configuration.program_name || 'AidKit Program';

  return (
    <div className={classNames(onLoginSuccess ? `` : `min-h-screen bg-gray-100`, `min-w-[180px]`)}>
      <AnnouncementBanner />
      <div className={classNames(`relative flex flex-col items-center md:flex-row md:items-start md:justify-center pb-2 px-2`, onLoginSuccess ? `md:pt-[3vh]` : `md:pt-[12vh]`)}>
        {onLoginSuccess ? null : <div className="my-[1rem] md:my-0 md:absolute md:top-4 md:left-4">
          <LanguageDropdown languages={(configuration.languages || 'en,es').split(',')} />
        </div>}
        <div className="w-full max-w-sm">
          <div className="bg-slate-800 px-6 py-2 rounded-t-lg border-l border-r border-t border-gray-300">
            <Paragraph size='sm' className="m-0 text-slate-200 text-center font-medium uppercase">
              {L.auth.admin_only}
            </Paragraph>
          </div>
          <div className="bg-white px-4 py-6 border-l border-r border-gray-300">
            <div className="mx-auto w-full">
              <div className="flex flex-col align-middle items-center mt-2 mb-4">
                {applicantFacingLogo ? (
                  <img
                    className="w-auto max-w-[180px] max-h-[100px]"
                    src={applicantFacingLogo}
                    alt={programName}
                  />
                ) : (
                  <AidKitLogo width={150} height={60} />
                )}
              </div>
              {['EmailOnly', 'SelectMethod', 'VerifyingCode', 'EnrollPassword', 'VerifyPassword'].includes(loginDisplayMode) && (
                <>
                  <Heading level="h3" className="text-center mb-1">
                    {L.auth.admin_login}
                  </Heading>
                  <Paragraph className="text-center mt-0 font-medium text-gray-700">
                    {L.auth.please_authenticate_continue}
                  </Paragraph>
                </>
              )}
            </div>
            {loginDisplay()}
          </div>
          <div className="bg-slate-50 px-2 py-1 rounded-b-lg sm:px-6 sm:py-6 border-l border-r border-b border-gray-300">
            <footer>
              <Paragraph size='xs' className="mt-10 text-gray-500 px-3">
                {L.auth.we_use_cookies}
              </Paragraph>
              <ul className="px-3 text-sm flex space-x-5">
                <li><Button variant="link" href="https://www.aidkit.com/privacy-policy">{L.privacy_policy}</Button></li>
                <li><Button variant="link" href="/tos.html">{L.terms_and_conditions}</Button></li>
              </ul>
            </footer>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthPage;
